import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Loader2, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { AnimatedSection, AnimatedCard } from '../components/AnimatedComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { settings } = useSiteSettings();
  const [searchParams] = useSearchParams();
  const contactInfo = settings?.contact_info || {};
  const [formData, setFormData] = useState({ 
    name: '', 
    origin: '',
    email: '', 
    phone: '', 
    adults: '2',
    children: '0',
    childrenAges: '',
    placesOfInterest: '',
    travelInterest: '',
    hotelPreference: '3-star',
    approximateBudget: '',
    message: '' 
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Office locations - from settings or defaults
  const officeLocations = settings?.office_locations || [
    { city: 'Chennai', address: 'No. 123, Anna Salai, Chennai - 600002', phone: '+91 44 2345 6789', is_head_office: true },
    { city: 'Madurai', address: 'No. 45, TPK Road, Madurai - 625001', phone: '+91 452 234 5678', is_head_office: false },
    { city: 'Coimbatore', address: 'No. 78, RS Puram, Coimbatore - 641002', phone: '+91 422 234 5678', is_head_office: false },
    { city: 'Cochin', address: 'No. 56, MG Road, Cochin - 682011', phone: '+91 484 234 5678', is_head_office: false },
    { city: 'Bengaluru', address: 'No. 89, MG Road, Bengaluru - 560001', phone: '+91 80 2345 6789', is_head_office: false },
    { city: 'Thiruvananthapuram', address: 'No. 34, MG Road, TVM - 695001', phone: '+91 471 234 5678', is_head_office: false },
    { city: 'Trichy', address: 'No. 12, Rockfort Road, Trichy - 620001', phone: '+91 431 234 5678', is_head_office: false },
  ];

  // Pre-fill message from AI Planner
  useEffect(() => {
    const prefillMessage = searchParams.get('message');
    const prefillSubject = searchParams.get('subject');
    if (prefillMessage) {
      setFormData(prev => ({ ...prev, message: decodeURIComponent(prefillMessage) }));
    }
  }, [searchParams]);

  // Check if message is from AI Planner
  const isAIPlanBooking = formData.message.includes('AI TRIP PLAN:') || formData.message.includes('AI Trip Plan Request');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    // Build comprehensive message with all fields
    let fullMessage = formData.message || '';
    if (!isAIPlanBooking) {
      fullMessage = `TRAVEL ENQUIRY DETAILS\n${'─'.repeat(40)}\n`;
      fullMessage += `Origin: ${formData.origin || 'Not specified'}\n`;
      fullMessage += `Travelers: ${formData.adults} Adults`;
      if (parseInt(formData.children) > 0) {
        fullMessage += `, ${formData.children} Children`;
        if (formData.childrenAges) fullMessage += ` (Ages: ${formData.childrenAges})`;
      }
      fullMessage += `\nPlaces of Interest: ${formData.placesOfInterest || 'Not specified'}\n`;
      fullMessage += `Travel Interest: ${formData.travelInterest || 'Not specified'}\n`;
      fullMessage += `Hotel Preference: ${formData.hotelPreference}\n`;
      fullMessage += `Approximate Budget: ${formData.approximateBudget || 'Not specified'}\n`;
      if (formData.message) fullMessage += `\nAdditional Notes:\n${formData.message}`;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/enquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          name: formData.name, 
          email: formData.email, 
          phone: formData.phone, 
          message: fullMessage, 
          enquiry_type: isAIPlanBooking ? 'ai_trip_plan' : 'general' 
        }),
      });
      if (!response.ok) throw new Error('Failed to submit enquiry');
      setSubmitted(true);
      setFormData({ 
        name: '', origin: '', email: '', phone: '', 
        adults: '2', children: '0', childrenAges: '',
        placesOfInterest: '', travelInterest: '', 
        hotelPreference: '3-star', approximateBudget: '', message: '' 
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit your enquiry. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${(contactInfo.whatsapp || '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent('Hello! I would like to enquire about your travel services.')}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-s-bg">
      <SEO 
        title="Contact Sentora Holidays | Book Your India Tour Today" 
        description="Get in touch with Sentora Holidays' travel experts. 40+ years of experience helping travelers plan their perfect India vacation. Call, email, or fill our enquiry form."
        keywords="contact Sentora Holidays, book tour, travel enquiry, India tour booking, holiday booking, travel consultation"
        url="https://www.sentoraholidays.com/contact"
      />

      {/* Hero Banner */}
      <section className="relative h-[35vh] md:h-[45vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&q=80)' }} />
        <div className="absolute inset-0 bg-gradient-to-r from-s-bg via-s-bg/90 to-s-bg/70" />
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="text-center px-4">
            <Badge style={{ backgroundColor: 'color-mix(in srgb, rgb(var(--color-accent)) 20%, transparent)' }} className="text-s-accent border-s-accent/30 mb-3 md:mb-4 text-xs badge-pop">
              <Clock size={12} className="mr-1" /> Est. 1985 · 40 Years
            </Badge>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-s-text mb-3 md:mb-4 font-heritage italic hero-text-reveal">Let's Create Your Experience</h1>
            <p className="text-sm md:text-lg text-s-text2 max-w-2xl mx-auto hero-text-reveal-delay">
              Visit any of our 7 offices across South India or reach out online.
            </p>
          </div>
        </div>
      </section>

      {/* Office Locations Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <AnimatedSection className="text-center mb-10">
          <Badge className="bg-s-primary/20 text-s-primary border-s-primary/30 mb-3">
            <Building2 size={14} className="mr-1" /> Our Offices
          </Badge>
          <h2 className="text-2xl md:text-3xl font-bold text-s-text font-heritage italic">Our Presence</h2>
        </AnimatedSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {officeLocations.map((office, idx) => (
            <AnimatedCard 
              key={idx}
              delay={idx * 80}
              hoverEffect="lift-subtle"
              className={`bg-s-card border-s-border rounded-xl ${office.is_head_office ? 'ring-2 ring-s-primary' : ''}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-s-text">{office.city}</h3>
                  {office.is_head_office && (
                    <Badge className="bg-s-primary text-white text-[10px]">Head Office</Badge>
                  )}
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2 text-s-text2">
                    <MapPin size={14} className="text-s-primary flex-shrink-0 mt-0.5" />
                    <span>{office.address}</span>
                  </div>
                  <div className="flex items-center gap-3 pt-2 border-t border-s-border">
                    <a 
                      href={`tel:${contactInfo.phone || ''}`} 
                      className="w-10 h-10 rounded-full bg-s-primary/20 flex items-center justify-center hover:bg-s-primary/30 transition-colors"
                      title="Call us"
                      data-testid={`office-call-${idx}`}
                    >
                      <Phone size={18} className="text-s-primary" />
                    </a>
                    <a 
                      href={`https://wa.me/${(contactInfo.whatsapp || '').replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-s-primary/20 flex items-center justify-center hover:bg-s-primary/30 transition-colors"
                      title="WhatsApp us"
                      data-testid={`office-whatsapp-${idx}`}
                    >
                      <svg viewBox="0 0 24 24" className="w-[18px] h-[18px] fill-s-primary">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </CardContent>
            </AnimatedCard>
          ))}
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="bg-s-bg2 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Form */}
            <AnimatedSection animation="fade-left">
              <Badge style={{ backgroundColor: 'color-mix(in srgb, rgb(var(--color-primary)) 20%, transparent)' }} className="text-s-primary border-s-primary/30 mb-4">Start Your Experience</Badge>
              <h2 className="text-2xl md:text-3xl font-bold text-s-text mb-4 font-heritage italic">Send Us a Message</h2>
              <p className="text-s-text2 mb-6">
                Share your travel wishes and our experience curators will design a complete journey.
              </p>

              {submitted ? (
                <Card className="bg-s-primary/10 border-s-primary/30">
                  <CardContent className="p-8 text-center">
                    <CheckCircle className="w-16 h-16 text-s-primary mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-s-text mb-2">Thank You!</h3>
                    <p className="text-s-text2 mb-4">Our experience curator will reach out within 24 hours.</p>
                    <Button onClick={() => setSubmitted(false)} className="bg-s-primary hover:bg-s-primary">Send Another Message</Button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-s-card border-s-border hover-lift-subtle transition-all">
                  <CardContent className="p-6">
                    {error && (
                      <div className="bg-s-primary/20 border border-red-500/30 text-s-primary px-4 py-3 rounded-lg mb-6">{error}</div>
                    )}
                    
                    {/* Show simplified form for AI Plan bookings */}
                    {isAIPlanBooking ? (
                      <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                          <Label htmlFor="name" className="text-s-text text-sm">Full Name *</Label>
                          <Input id="name" data-testid="contact-name-input" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Doe" className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted h-11" required />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="email" className="text-s-text text-sm">Email Address *</Label>
                            <Input id="email" data-testid="contact-email-input" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@example.com" className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted h-11" required />
                          </div>
                          <div>
                            <Label htmlFor="phone" className="text-s-text text-sm">Phone Number *</Label>
                            <Input id="phone" data-testid="contact-phone-input" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+91 98765 43210" className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted h-11" required />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="message" className="text-s-text text-sm flex items-center gap-2">
                            AI Trip Plan <Badge className="bg-s-primary/20 text-s-primary text-xs">Attached</Badge>
                          </Label>
                          <Textarea 
                            id="message" 
                            data-testid="contact-message-input" 
                            value={formData.message} 
                            onChange={(e) => setFormData({...formData, message: e.target.value})} 
                            className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted min-h-[300px] font-mono text-xs" 
                            required 
                          />
                          <p className="text-xs text-s-muted mt-1">Your AI-generated trip plan is attached above.</p>
                        </div>
                        <Button type="submit" disabled={isSubmitting} className="w-full bg-s-primary hover:bg-s-primary-h h-12" data-testid="contact-submit-btn">
                          {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Book This Trip</>}
                        </Button>
                      </form>
                    ) : (
                      /* Full enquiry form with all fields */
                      <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Name & Origin */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="name" className="text-s-text text-sm">Full Name *</Label>
                            <Input id="name" data-testid="contact-name-input" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Doe" className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted h-11" required />
                          </div>
                          <div>
                            <Label htmlFor="origin" className="text-s-text text-sm">Origin City *</Label>
                            <Input id="origin" value={formData.origin} onChange={(e) => setFormData({...formData, origin: e.target.value})} placeholder="Mumbai, Delhi, Chennai..." className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted h-11" required />
                          </div>
                        </div>
                        
                        {/* Email & Phone */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="email" className="text-s-text text-sm">Email Address *</Label>
                            <Input id="email" data-testid="contact-email-input" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@example.com" className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted h-11" required />
                          </div>
                          <div>
                            <Label htmlFor="phone" className="text-s-text text-sm">Contact Number *</Label>
                            <Input id="phone" data-testid="contact-phone-input" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+91 98765 43210" className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted h-11" required />
                          </div>
                        </div>
                        
                        {/* Travelers */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <Label htmlFor="adults" className="text-s-text text-sm">Adults *</Label>
                            <select id="adults" value={formData.adults} onChange={(e) => setFormData({...formData, adults: e.target.value})} className="mt-1.5 w-full h-11 px-3 rounded-md bg-s-bg border border-s-border text-s-text">
                              {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="children" className="text-s-text text-sm">Children</Label>
                            <select id="children" value={formData.children} onChange={(e) => setFormData({...formData, children: e.target.value})} className="mt-1.5 w-full h-11 px-3 rounded-md bg-s-bg border border-s-border text-s-text">
                              {[0,1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                          </div>
                          {parseInt(formData.children) > 0 && (
                            <div>
                              <Label htmlFor="childrenAges" className="text-s-text text-sm">Children Ages</Label>
                              <Input id="childrenAges" value={formData.childrenAges} onChange={(e) => setFormData({...formData, childrenAges: e.target.value})} placeholder="e.g., 5, 8" className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted h-11" />
                            </div>
                          )}
                        </div>
                        
                        {/* Places of Interest */}
                        <div>
                          <Label htmlFor="placesOfInterest" className="text-s-text text-sm">Places of Interest *</Label>
                          <Input id="placesOfInterest" value={formData.placesOfInterest} onChange={(e) => setFormData({...formData, placesOfInterest: e.target.value})} placeholder="e.g., Kerala, Rajasthan, Goa..." className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted h-11" required />
                        </div>
                        
                        {/* Travel Interest */}
                        <div>
                          <Label htmlFor="travelInterest" className="text-s-text text-sm">Specific Travel Interest</Label>
                          <Input id="travelInterest" value={formData.travelInterest} onChange={(e) => setFormData({...formData, travelInterest: e.target.value})} placeholder="e.g., Heritage, Beach, Adventure, Wildlife, Pilgrimage..." className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted h-11" />
                        </div>
                        
                        {/* Hotel & Budget */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="hotelPreference" className="text-s-text text-sm">Hotel Preference *</Label>
                            <select id="hotelPreference" value={formData.hotelPreference} onChange={(e) => setFormData({...formData, hotelPreference: e.target.value})} className="mt-1.5 w-full h-11 px-3 rounded-md bg-s-bg border border-s-border text-s-text">
                              <option value="3-star">3 Star</option>
                              <option value="4-star">4 Star</option>
                              <option value="5-star">5 Star</option>
                              <option value="heritage">Heritage Property</option>
                              <option value="luxury">Luxury Resort</option>
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="approximateBudget" className="text-s-text text-sm">Approximate Budget</Label>
                            <Input id="approximateBudget" value={formData.approximateBudget} onChange={(e) => setFormData({...formData, approximateBudget: e.target.value})} placeholder="e.g., ₹50,000 per person" className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted h-11" />
                          </div>
                        </div>
                        
                        {/* Additional Notes */}
                        <div>
                          <Label htmlFor="message" className="text-s-text text-sm">Additional Requirements</Label>
                          <Textarea 
                            id="message" 
                            data-testid="contact-message-input" 
                            value={formData.message} 
                            onChange={(e) => setFormData({...formData, message: e.target.value})} 
                            placeholder="Any special requests, dietary requirements, accessibility needs..." 
                            className="mt-1.5 bg-s-bg border-s-border text-s-text placeholder:text-s-muted min-h-[80px]" 
                          />
                        </div>
                        
                        <Button type="submit" disabled={isSubmitting} className="w-full bg-s-primary hover:bg-s-primary-h h-12" data-testid="contact-submit-btn">
                          {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Submit Enquiry</>}
                        </Button>
                      </form>
                    )}
                  </CardContent>
                </Card>
              )}
            </AnimatedSection>

            {/* Contact Info */}
            <AnimatedSection animation="fade-right" delay={200} className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-s-text mb-4">Get in Touch Directly</h3>
                <div className="space-y-4">
                  <Card className="bg-s-card border-s-border hover-lift-subtle transition-all">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-s-primary/20 flex items-center justify-center">
                        <Phone className="text-s-primary" size={22} />
                      </div>
                      <div>
                        <p className="text-s-muted text-sm">Call Us</p>
                        <a href={`tel:${contactInfo.phone}`} className="text-s-text font-semibold text-lg hover:text-s-primary">{contactInfo.phone}</a>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-s-card border-s-border hover-lift-subtle transition-all">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-s-primary/20 flex items-center justify-center">
                        <Mail className="text-s-primary" size={22} />
                      </div>
                      <div>
                        <p className="text-s-muted text-sm">Email Us</p>
                        <a href={`mailto:${contactInfo.email}`} className="text-s-text font-semibold text-lg hover:text-s-primary">{contactInfo.email}</a>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-s-card border-s-border hover-lift-subtle transition-all">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-s-primary/20 flex items-center justify-center">
                        <Clock className="text-s-primary" size={22} />
                      </div>
                      <div>
                        <p className="text-s-muted text-sm">Working Hours</p>
                        <p className="text-s-text font-semibold">{contactInfo.working_hours}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <Card className="bg-s-primary border-none overflow-hidden hover-lift-subtle transition-all">
                <CardContent className="p-6 text-center">
                  <svg viewBox="0 0 24 24" className="w-12 h-12 fill-white mx-auto mb-3">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <h3 className="text-white text-xl font-bold mb-2">Chat on WhatsApp</h3>
                  <p className="text-s-primary text-sm mb-4">Get instant responses from our travel experts</p>
                  <Button onClick={handleWhatsApp} className="bg-white text-s-primary hover:bg-s-primary font-bold px-6 btn-animate">
                    Start Chat
                  </Button>
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
